import { gql, useApolloClient, useMutation, useQuery } from '@apollo/client';
import { yupResolver } from '@hookform/resolvers/yup';
import { string, func } from 'prop-types';
import { useEffect, useMemo } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import * as yup from 'yup';

import Button from 'components/common/Button';
import FormTitle from 'components/form/FormTitle';
import useToasts from 'hooks/useToasts';
import scrollToFormError from 'lib/scrollToFormError';
import { makeNullable } from 'lib/validators';
import ControlledField from '../form/ControlledField';
import FormNumberInput from '../form/FormNumberInput';

const GET_PARTICIPANT_DETAILS = gql`
  query GetParticipantEventDayInfo($campaignId: String!, $participantId: String!) {
    findCampaigns(id: $campaignId) {
      id
      eventNumberMode
      participants(where: { id: $participantId }) {
        id
        eventNumber
      }
    }
  }
`;

const UPDATE_PARTICIPANT = gql`
  mutation UpdateEventDay($data: FundraiserInput!) {
    updateParticipant(Fundraiser: $data) {
      id
      eventNumber
    }
  }
`;

const AdminFundraiserEditModalEventDayView = ({
  participantId,
  campaignId,
  onDirtyChange,
  onCancel,
}) => {
  const client = useApolloClient();
  const toast = useToasts('editParticipant:eventDay');

  const { data } = useQuery(GET_PARTICIPANT_DETAILS, {
    variables: {
      campaignId,
      participantId,
    },
  });

  const campaign = useMemo(() => data?.findCampaigns[0], [data]);
  const participant = useMemo(() => campaign?.participants[0], [campaign]);

  const validationSchema = yup.object().shape({
    eventNumber: makeNullable(yup.number().positive('Please enter a valid number')),
  });

  const formMethods = useForm({
    resolver: yupResolver(validationSchema),
  });
  const { handleSubmit, reset, formState, setError } = formMethods;
  const { isDirty } = formState;

  const [updateParticipant] = useMutation(UPDATE_PARTICIPANT, {
    onCompleted: () =>
      toast.success(
        campaign.eventNumberMode === 'automatic'
          ? 'Participant Event Number updated successfully. Numbers for new registrations will increment from the highest value assigned.'
          : 'Participant Event Number updated successfully.'
      ),
    onError: (err) => {
      if (err.message === 'DuplicateEventNumber') {
        setError('eventNumber', {
          message: 'This number is already in use.',
        });
        return;
      }
      toast.error('There was an error updating Participant Event Number, please try again.');
    },
  });

  useEffect(() => {
    reset({ eventNumber: participant?.eventNumber });
  }, [reset, participant]);

  // Prevent navigating with unsaved changes
  useEffect(() => onDirtyChange(isDirty), [onDirtyChange, isDirty]);

  if (!participant) return null;

  return (
    <form
      onSubmit={handleSubmit(async (values) => {
        await updateParticipant({
          variables: {
            data: {
              id: participantId,
              ...values,
            },
          },
        });
        client.refetchQueries({
          include: ['GetParticipantOverview'],
        });
      }, scrollToFormError)}
    >
      <FormTitle>Event</FormTitle>
      <FormProvider {...formMethods}>
        <ControlledField name="eventNumber">
          <ControlledField.Label isRequired>Event Number</ControlledField.Label>
          <ControlledField.Control as={FormNumberInput} />
          <ControlledField.ErrorMessage />
        </ControlledField>
      </FormProvider>
      <div className="mt-6 md:mt-8 grid grid-cols-2 md:grid-cols-1 gap-x-4">
        <Button
          as="button"
          type="button"
          color="gray-300"
          padding="sm"
          className="font-medium w-full md:hidden"
          onClick={onCancel}
          outline
        >
          Cancel
        </Button>
        <div className="flex justify-end">
          <Button
            as="button"
            type="submit"
            color="primary"
            padding="sm"
            className="font-medium w-full md:w-auto"
          >
            Save
          </Button>
        </div>
      </div>
    </form>
  );
};

AdminFundraiserEditModalEventDayView.propTypes = {
  campaignId: string.isRequired,
  participantId: string.isRequired,
  onDirtyChange: func.isRequired,
  onCancel: func.isRequired,
};

AdminFundraiserEditModalEventDayView.defaultProps = {};

export default AdminFundraiserEditModalEventDayView;
